import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, forwardRef, cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterModal } from './ModalManager';
import ModalPortal from './ModalPortal';
import { useOverlayModal } from '../../../contexts/VideoModalContext';

const ENTER_MOTION_CLASS = { rise: 'kaiylo-dialog-in', offset: 'kaiylo-dialog-in-offset' };

const BaseModal = forwardRef(({
  isOpen,
  onClose,
  title,
  titleIcon,
  headerActions,
  children,
  modalId,
  zIndex = 50,
  closeOnEsc = true,
  closeOnBackdrop = true,
  onBackdropClick,
  className = '',
  // Inline styles merged onto the dialog box (after its own background), e.g.
  // a computed max-width when the caller lays out side panels around it.
  style: modalStyle,
  size = 'md',
  noPadding = false,
  footer,
  titleClassName = 'text-xl font-semibold text-white',
  externalContent,
  borderRadius,
  onModalBodyScroll,
  // Animation d'ouverture (voir index.css) : 'rise' (transform) par défaut ;
  // 'offset' quand la modale contient un volet position: fixed (un transform
  // le déplacerait pendant l'animation) ; 'none' pour la couper.
  enterMotion = 'rise',
  // La modale suit la hauteur de son contenu en glissant (ajout d'un bloc,
  // données qui arrivent) au lieu de sauter d'une taille à l'autre.
  animateHeight = false,
  // Phone screens (opt-in, e.g. the coach session editor under 768px): the
  // dialog takes the whole viewport, no backdrop margin nor rounded corners,
  // and its body fills whatever the header and footer leave.
  fullScreen = false,
  // Header kept OUT of the scrolling body (the `title` header is too, but
  // only takes a title). With `footer`, the body scrolls between the two.
  fixedHeader,
}, ref) => {
  const { t } = useTranslation();
  const { isTopMost } = useRegisterModal(modalId);
  const { registerModalOpen, registerModalClose } = useOverlayModal();
  const internalModalRef = useRef(null);

  // Hide coach mobile bottom nav when this modal is open
  useEffect(() => {
    if (isOpen) {
      registerModalOpen();
      return () => registerModalClose();
    }
  }, [isOpen, registerModalOpen, registerModalClose]);
  const modalRef = ref || internalModalRef;
  const backdropRef = useRef(null);
  const externalContentRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store the element that opened the modal
  useEffect(() => {
    if (isOpen && !previousActiveElement.current) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && isTopMost && modalRef.current) {
      // Focus the modal
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [isOpen, isTopMost]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isOpen]);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isTopMost && closeOnEsc) {
      onClose();
    }
  }, [isTopMost, closeOnEsc, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Lock body scroll when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // animateHeight : hauteur du contenu, suivie en continu. La première mesure
  // s'applique sans transition (la modale s'ouvre déjà à la bonne taille).
  // Ensuite, un vrai saut (bloc de données qui arrive d'un coup) glisse, mais
  // les petits pas d'un contenu qui s'anime lui-même (bloc qui se déplie,
  // image par image) sont suivis tels quels : une transition sur chacun
  // ferait traîner la modale derrière son contenu.
  const contentRef = useRef(null);
  const lastContentHeightRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(null);
  const [heightTransition, setHeightTransition] = useState(false);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!animateHeight || !isOpen || !el) return undefined;
    lastContentHeightRef.current = el.offsetHeight;
    setContentHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return undefined;
    let lastChangeAt = 0;
    const ro = new ResizeObserver(() => {
      const next = el.offsetHeight;
      const prev = lastContentHeightRef.current;
      const now = performance.now();
      // Changements image après image = contenu qui s'anime : on le suit.
      const continuous = now - lastChangeAt < 80;
      lastChangeAt = now;
      lastContentHeightRef.current = next;
      setHeightTransition(!continuous && prev != null && Math.abs(next - prev) > 24);
      setContentHeight(next);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      lastContentHeightRef.current = null;
      setHeightTransition(false);
      setContentHeight(null);
    };
  }, [animateHeight, isOpen]);

  // Handle backdrop: only close when mousedown happened on backdrop (not when user releases there after selecting text inside modal)
  const handleBackdropMouseDown = useCallback((e) => {
    const isExternalContentClick = externalContentRef.current && externalContentRef.current.contains(e.target);
    const isModalContentClick = modalRef.current && modalRef.current.contains(e.target);
    const isBackdropClick = e.target === backdropRef.current ||
                           (backdropRef.current && backdropRef.current.contains(e.target) &&
                            !isModalContentClick && !isExternalContentClick);

    if (isBackdropClick) {
      if (onBackdropClick) {
        onBackdropClick(e);
      } else if (isTopMost && closeOnBackdrop) {
        onClose();
      }
    }
  }, [isTopMost, closeOnBackdrop, onClose, onBackdropClick]);

  if (!isOpen) {
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl'
  };

  return (
    <ModalPortal zIndex={zIndex}>
      <div
        ref={backdropRef}
        className={`fixed inset-0 bg-black/60 backdrop-blur flex items-center justify-center ${fullScreen ? 'p-0' : 'p-4'} ${enterMotion === 'none' ? '' : 'kaiylo-backdrop-in'}`}
        onMouseDown={handleBackdropMouseDown}
        style={{ zIndex: zIndex || 100 }}
      >
        <div className="relative w-full overflow-visible">
          {/* External content - rendered outside modal but inside relative container */}
          {externalContent && isValidElement(externalContent) 
            ? cloneElement(externalContent, { ref: externalContentRef })
            : externalContent
          }
          <div
            ref={modalRef}
            className={`relative mx-auto w-full ${ENTER_MOTION_CLASS[enterMotion] || ''} ${
              fullScreen
                ? 'h-[100dvh] max-h-[100dvh] max-w-none min-w-0 rounded-none'
                : `${sizeClasses[size]} min-w-[min(500px,calc(100vw-2rem))] max-h-[92vh] ${borderRadius ? '' : 'rounded-2xl'}`
            } overflow-hidden shadow-2xl flex flex-col ${className}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? `${modalId}-title` : undefined}
            style={fullScreen ? {
              // A whole screen: opaque, the page behind must not show through.
              background: '#131416',
              ...modalStyle
            } : {
              background: 'linear-gradient(90deg, rgba(19, 20, 22, 1) 0%, rgba(43, 44, 48, 1) 61%, rgba(65, 68, 72, 0.75) 100%)',
              opacity: 0.95,
              ...(borderRadius && { borderRadius }),
              ...modalStyle
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
          {/* Header */}
          {title && (
            <>
              <div className="shrink-0 px-4 md:px-6 pt-4 md:pt-6 pb-3 flex items-center justify-between">
                <h2 id={`${modalId}-title`} className={`${titleClassName} flex items-center gap-2`} style={{ color: 'var(--kaiylo-primary-hex)' }}>
                  {titleIcon}
                  {title}
                </h2>
                <div className="flex shrink-0 items-center gap-3">
                {/* Extra icons left of the close cross (e.g. a delete action). */}
                {headerActions}
                <button
                  onClick={onClose}
                  className="text-white/50 hover:text-white transition-colors shrink-0"
                  aria-label={t('buttons.close')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5" fill="currentColor">
                    <path d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z"/>
                  </svg>
                </button>
                </div>
              </div>
              <div className="border-b border-white/10 mx-4 md:mx-6"></div>
            </>
          )}

          {fixedHeader && <div className="shrink-0">{fixedHeader}</div>}

          {/* Scrollable Body */}
          <div
            className={`flex-1 min-h-0 overflow-y-auto overscroll-contain modal-scrollable-body ${
              noPadding || animateHeight ? '' : 'px-4 md:px-6 py-4 md:py-6 space-y-5'
            } ${footer && !animateHeight ? 'pb-4' : ''}`}
            style={fullScreen ? {
              WebkitOverflowScrolling: 'touch',
              flex: '1 1 0%',
            } : {
              scrollbarGutter: 'stable',
              WebkitOverflowScrolling: 'touch',
              maxHeight: title && footer 
                ? 'calc(92vh - 73px - 80px)' 
                : title 
                  ? 'calc(92vh - 73px)' 
                  : footer 
                    ? 'calc(92vh - 80px)' 
                    : '92vh',
              height: title && footer 
                ? 'calc(92vh - 73px - 80px)' 
                : title 
                  ? 'calc(92vh - 73px)' 
                  : footer 
                    ? 'calc(92vh - 80px)' 
                    : '92vh',
              // Taille de base = hauteur du contenu (bornée par maxHeight), qui glisse.
              ...(animateHeight && contentHeight != null && {
                flex: `1 1 ${contentHeight}px`,
                transition: heightTransition ? 'flex-basis 300ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
              }),
            }}
            onScroll={onModalBodyScroll}
          >
            {animateHeight ? (
              <div
                ref={contentRef}
                className={`${noPadding ? '' : 'px-4 md:px-6 py-4 md:py-6 space-y-5'} ${footer && !noPadding ? 'pb-4' : ''}`}
              >
                {children}
              </div>
            ) : children}
          </div>

          {/* Footer */}
          {footer && (
            <div
              className={fullScreen
                ? 'shrink-0 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] border-t border-white/10 bg-[#0f0f10]/95 backdrop-blur'
                : 'shrink-0 px-4 md:px-6 py-4 border-t border-white/10 bg-[#0f0f10]/95 backdrop-blur pb-[max(0px,env(safe-area-inset-bottom))]'}
            >
              {footer}
            </div>
          )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
});

BaseModal.displayName = 'BaseModal';

export default BaseModal;
